// Pure metric helpers operating on the raw `businesses/<id>` RTDB snapshot.
// No Firebase, no Vue, no async - every export is a synchronous function so
// the dashboard can call them once per snapshot fetch and so unit tests (when
// added) can hit them in isolation.
//
// Defensive design rules:
//  - Never throw on missing / empty / malformed data - return zeros, [],
//    or { categories: [], series: [] } as appropriate.
//  - Accept `now` as a parameter so deterministic tests can pass a fixed
//    timestamp; default to `Date.now()` for production.
//  - Single-pass aggregation per dataset where feasible (the dashboard may
//    be rendering 5000+ stamps).

// ---------------------------------------------------------------------------
// Data hygiene primitives
// ---------------------------------------------------------------------------

const MS_PER_SECOND = 1000;
const MS_PER_MINUTE = 60 * 1000;
const MS_PER_HOUR = 60 * MS_PER_MINUTE;
const MS_PER_DAY = 24 * MS_PER_HOUR;

// RTDB stores some customer emails as keys with `.` and some with `&`
// (the iOS legacy sanitisation). To group reliably we canonicalise both
// shapes to one form - replace `&` with `.` so it reads naturally.
export const normaliseEmailKey = (key) => {
  if (key == null) return "";
  return String(key).replace(/&/g, ".").toLowerCase();
};

// RTDB silently turns sparse arrays into objects keyed by stringified
// indices. This coerces either shape into a dense JS array.
export const coerceArrayLike = (value) => {
  if (value == null) return [];
  if (Array.isArray(value)) return value.filter((v) => v != null);
  if (typeof value !== "object") return [];
  return Object.values(value).filter((v) => v != null);
};

// Timestamps in production are Unix seconds. Reject NaN, non-finite,
// non-positive, and anything past year-2100 (corrupt rows).
const MAX_VALID_SECONDS = 4102444800; // 2100-01-01T00:00:00Z

export const isValidTimestamp = (seconds) => {
  if (typeof seconds !== "number") return false;
  if (!Number.isFinite(seconds)) return false;
  if (seconds <= 0) return false;
  if (seconds > MAX_VALID_SECONDS) return false;
  return true;
};

export const secondsToMs = (seconds) => seconds * MS_PER_SECOND;

// Filters rows missing any of the required keys.
export const safeRow = (row, requiredKeys) => {
  if (!row || typeof row !== "object") return false;
  for (const key of requiredKeys) {
    if (row[key] === undefined || row[key] === null) return false;
  }
  return true;
};

// ---------------------------------------------------------------------------
// Internal helpers (not exported)
// ---------------------------------------------------------------------------

// Returns the local-midnight Date for the start of the day containing `nowMs`.
const startOfLocalDay = (nowMs) => {
  const d = new Date(nowMs);
  d.setHours(0, 0, 0, 0);
  return d;
};

// Normalise a single stamp row into { emailKey, ms } or null if invalid.
// Stamps in production look like: { customer: <email-key>, timestamp: <sec>,
// activity?: <string> }. iOS code reads `customer` so we follow suit but
// also tolerate `email` as a fallback.
const normaliseStamp = (row) => {
  if (!row || typeof row !== "object") return null;
  const seconds = row.timestamp;
  if (!isValidTimestamp(seconds)) return null;
  const rawKey = row.customer || row.email || row.customerEmail;
  if (rawKey == null) return null;
  const emailKey = normaliseEmailKey(rawKey);
  if (!emailKey) return null;
  return {
    emailKey,
    ms: secondsToMs(seconds),
    activity: typeof row.activity === "string" ? row.activity : "",
    rawEmail: String(rawKey),
  };
};

// Same normaliser for redeems.
const normaliseRedeem = (row) => {
  if (!row || typeof row !== "object") return null;
  const seconds = row.timestamp;
  if (!isValidTimestamp(seconds)) return null;
  const rawKey = row.customer || row.email || row.customerEmail;
  if (rawKey == null) return null;
  const emailKey = normaliseEmailKey(rawKey);
  if (!emailKey) return null;
  return { emailKey, ms: secondsToMs(seconds) };
};

// `customers` is an object keyed by email-key. Returns an array of
// { emailKey, raw } pairs so callers can read both the canonical key
// and the original `registeredOn`/`eligibleToRedeemNow` payload.
const flattenCustomers = (customers) => {
  if (!customers || typeof customers !== "object") return [];
  const out = [];
  for (const [key, value] of Object.entries(customers)) {
    if (!key) continue;
    out.push({
      emailKey: normaliseEmailKey(key),
      raw: value && typeof value === "object" ? value : {},
    });
  }
  return out;
};

// Parse `registeredOn` which can be an ISO string OR Unix seconds (iOS
// legacy dual format). Returns ms or null.
const parseRegisteredOn = (value) => {
  if (value == null) return null;
  if (typeof value === "number") {
    if (!isValidTimestamp(value)) return null;
    return secondsToMs(value);
  }
  if (typeof value === "string") {
    const ms = Date.parse(value);
    if (Number.isFinite(ms) && ms > 0) return ms;
    // Some rows store the numeric seconds as a string.
    const asNum = Number(value);
    if (Number.isFinite(asNum) && isValidTimestamp(asNum)) {
      return secondsToMs(asNum);
    }
  }
  return null;
};

// Day-key (yyyy-mm-dd) for local-timezone bucketing - cheap string join,
// avoids constructing intermediate Date objects in tight loops.
const localDayKey = (ms) => {
  const d = new Date(ms);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
};

const localMonthKey = (ms) => {
  const d = new Date(ms);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
};

// ISO week start (Monday 00:00 local) for the date containing `ms`.
const startOfIsoWeek = (ms) => {
  const d = new Date(ms);
  d.setHours(0, 0, 0, 0);
  const dow = d.getDay(); // 0=Sun..6=Sat
  const offset = dow === 0 ? -6 : 1 - dow; // shift to Monday
  d.setDate(d.getDate() + offset);
  return d;
};

const formatIsoWeekLabel = (weekStart) => {
  const months = [
    "Jan", "Feb", "Mar", "Apr", "May", "Jun",
    "Jul", "Aug", "Sep", "Oct", "Nov", "Dec",
  ];
  return `${months[weekStart.getMonth()]} ${weekStart.getDate()}`;
};

const formatMonthLabel = (monthDate) => {
  // e.g. "Jan 25"
  const months = [
    "Jan",
    "Feb",
    "Mar",
    "Apr",
    "May",
    "Jun",
    "Jul",
    "Aug",
    "Sep",
    "Oct",
    "Nov",
    "Dec",
  ];
  const yy = String(monthDate.getFullYear()).slice(-2);
  return `${months[monthDate.getMonth()]} ${yy}`;
};

// ---------------------------------------------------------------------------
// Primary KPIs
// ---------------------------------------------------------------------------

export const scansToday = (stamps, now = Date.now()) => {
  const list = coerceArrayLike(stamps);
  const midnight = startOfLocalDay(now).getTime();
  let count = 0;
  for (let i = 0; i < list.length; i++) {
    const n = normaliseStamp(list[i]);
    if (n && n.ms >= midnight) count++;
  }
  return count;
};

export const scansLast7Days = (stamps, now = Date.now()) => {
  const list = coerceArrayLike(stamps);
  const cutoff = now - 7 * MS_PER_DAY;
  let count = 0;
  for (let i = 0; i < list.length; i++) {
    const n = normaliseStamp(list[i]);
    if (n && n.ms >= cutoff) count++;
  }
  return count;
};

export const totalScans = (stamps) => {
  const list = coerceArrayLike(stamps);
  let count = 0;
  for (let i = 0; i < list.length; i++) {
    if (normaliseStamp(list[i])) count++;
  }
  return count;
};

export const customersEnrolled = (customers) => {
  const seen = new Set();
  for (const c of flattenCustomers(customers)) {
    if (c.emailKey) seen.add(c.emailKey);
  }
  return seen.size;
};

export const totalRedemptions = (redeems) => {
  const list = coerceArrayLike(redeems);
  let count = 0;
  for (let i = 0; i < list.length; i++) {
    if (normaliseRedeem(list[i])) count++;
  }
  return count;
};

export const dueToRedeemNow = (customers) => {
  let count = 0;
  for (const c of flattenCustomers(customers)) {
    if (c.raw && c.raw.eligibleToRedeemNow === true) count++;
  }
  return count;
};

// ---------------------------------------------------------------------------
// Secondary KPIs
// ---------------------------------------------------------------------------

const stampsByCustomer = (stamps) => {
  const list = coerceArrayLike(stamps);
  const counts = new Map();
  for (let i = 0; i < list.length; i++) {
    const n = normaliseStamp(list[i]);
    if (!n) continue;
    counts.set(n.emailKey, (counts.get(n.emailKey) || 0) + 1);
  }
  return counts;
};

export const repeatCustomerRate = (stamps) => {
  const counts = stampsByCustomer(stamps);
  if (counts.size === 0) return 0;
  let repeats = 0;
  for (const n of counts.values()) {
    if (n > 1) repeats++;
  }
  return repeats / counts.size;
};

export const avgStampsPerCustomer = (stamps) => {
  const counts = stampsByCustomer(stamps);
  if (counts.size === 0) return 0;
  let total = 0;
  for (const n of counts.values()) total += n;
  return total / counts.size;
};

// Share of enrolled customers who have redeemed at least once. Counts people, not
// redemptions, so a customer redeeming twice can't push the rate past 100%.
// Redeemers missing from the customer list are left out for the same reason.
export const redemptionRate = (redeems, customers) => {
  const enrolledKeys = new Set();
  for (const c of flattenCustomers(customers)) {
    if (c.emailKey) enrolledKeys.add(c.emailKey);
  }
  if (enrolledKeys.size === 0) return 0;
  const redeemList = coerceArrayLike(redeems);
  const redeemedKeys = new Set();
  for (let i = 0; i < redeemList.length; i++) {
    const r = normaliseRedeem(redeemList[i]);
    if (r && enrolledKeys.has(r.emailKey)) redeemedKeys.add(r.emailKey);
  }
  return redeemedKeys.size / enrolledKeys.size;
};

export const activeCustomersLast30Days = (stamps, now = Date.now()) => {
  const list = coerceArrayLike(stamps);
  const cutoff = now - 30 * MS_PER_DAY;
  const seen = new Set();
  for (let i = 0; i < list.length; i++) {
    const n = normaliseStamp(list[i]);
    if (n && n.ms >= cutoff) seen.add(n.emailKey);
  }
  return seen.size;
};

// ---------------------------------------------------------------------------
// Chart series - every fn returns { categories, series } for ApexCharts.
// ---------------------------------------------------------------------------

// Daily scans for the last N days (inclusive of today).
export const scanActivityOverTime = (stamps, rangeDays, now = Date.now()) => {
  const list = coerceArrayLike(stamps);
  const days = Number.isFinite(rangeDays) && rangeDays > 0 ? Math.floor(rangeDays) : null;

  // If days is null, return everything bucketed by day.
  const buckets = new Map();
  let minMs = Infinity;
  let maxMs = -Infinity;

  let cutoff = -Infinity;
  if (days) cutoff = startOfLocalDay(now - (days - 1) * MS_PER_DAY).getTime();

  for (let i = 0; i < list.length; i++) {
    const n = normaliseStamp(list[i]);
    if (!n) continue;
    if (n.ms < cutoff) continue;
    const key = localDayKey(n.ms);
    buckets.set(key, (buckets.get(key) || 0) + 1);
    if (n.ms < minMs) minMs = n.ms;
    if (n.ms > maxMs) maxMs = n.ms;
  }

  // Generate the full continuous day range so the chart shows zero days.
  const categories = [];
  const series = [];
  if (days) {
    const startDate = startOfLocalDay(now - (days - 1) * MS_PER_DAY);
    for (let i = 0; i < days; i++) {
      const d = new Date(startDate.getTime() + i * MS_PER_DAY);
      const key = localDayKey(d.getTime());
      categories.push(key);
      series.push(buckets.get(key) || 0);
    }
  } else if (Number.isFinite(minMs) && Number.isFinite(maxMs)) {
    const startDate = startOfLocalDay(minMs);
    const endDate = startOfLocalDay(maxMs);
    const totalDays = Math.round((endDate.getTime() - startDate.getTime()) / MS_PER_DAY) + 1;
    for (let i = 0; i < totalDays; i++) {
      const d = new Date(startDate.getTime() + i * MS_PER_DAY);
      const key = localDayKey(d.getTime());
      categories.push(key);
      series.push(buckets.get(key) || 0);
    }
  }

  return {
    categories,
    series: [{ name: "Scans", data: series }],
  };
};

export const scansVsRedemptions12Weeks = (stamps, redeems, now = Date.now()) => {
  const weeks = 12;
  const startOfThisWeek = startOfIsoWeek(now);
  const startMs = startOfThisWeek.getTime() - (weeks - 1) * 7 * MS_PER_DAY;

  const scansArr = new Array(weeks).fill(0);
  const redeemsArr = new Array(weeks).fill(0);

  const stampList = coerceArrayLike(stamps);
  for (let i = 0; i < stampList.length; i++) {
    const n = normaliseStamp(stampList[i]);
    if (!n) continue;
    const bucket = Math.floor((n.ms - startMs) / (7 * MS_PER_DAY));
    if (bucket >= 0 && bucket < weeks) scansArr[bucket]++;
  }
  const redeemList = coerceArrayLike(redeems);
  for (let i = 0; i < redeemList.length; i++) {
    const n = normaliseRedeem(redeemList[i]);
    if (!n) continue;
    const bucket = Math.floor((n.ms - startMs) / (7 * MS_PER_DAY));
    if (bucket >= 0 && bucket < weeks) redeemsArr[bucket]++;
  }

  const categories = [];
  for (let i = 0; i < weeks; i++) {
    const weekStart = new Date(startMs + i * 7 * MS_PER_DAY);
    categories.push(formatIsoWeekLabel(weekStart));
  }

  return {
    categories,
    series: [
      { name: "Scans", data: scansArr },
      { name: "Redemptions", data: redeemsArr },
    ],
  };
};

export const newSignupsByMonth = (customers, now = Date.now()) => {
  const months = 12;
  const nowDate = new Date(now);
  const firstOfThisMonth = new Date(nowDate.getFullYear(), nowDate.getMonth(), 1);
  const monthKeys = [];
  const monthLabels = [];
  for (let i = months - 1; i >= 0; i--) {
    const d = new Date(firstOfThisMonth.getFullYear(), firstOfThisMonth.getMonth() - i, 1);
    monthKeys.push(localMonthKey(d.getTime()));
    monthLabels.push(formatMonthLabel(d));
  }
  const keyIndex = new Map(monthKeys.map((k, i) => [k, i]));
  const data = new Array(months).fill(0);

  for (const c of flattenCustomers(customers)) {
    const ms = parseRegisteredOn(c.raw.registeredOn);
    if (ms == null) continue;
    const key = localMonthKey(ms);
    const idx = keyIndex.get(key);
    if (idx != null) data[idx]++;
  }

  return {
    categories: monthLabels,
    series: [{ name: "New customers", data }],
  };
};

export const busiestDayOfWeek = (stamps) => {
  // Display Mon-Sun order. JS getDay returns 0=Sun..6=Sat.
  const labels = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
  const data = [0, 0, 0, 0, 0, 0, 0];
  const list = coerceArrayLike(stamps);
  for (let i = 0; i < list.length; i++) {
    const n = normaliseStamp(list[i]);
    if (!n) continue;
    const dow = new Date(n.ms).getDay();
    const idx = dow === 0 ? 6 : dow - 1;
    data[idx]++;
  }
  return {
    categories: labels,
    series: [{ name: "Scans", data }],
  };
};

export const busiestHourOfDay = (stamps) => {
  const categories = [];
  for (let h = 0; h < 24; h++) categories.push(`${String(h).padStart(2, "0")}:00`);
  const data = new Array(24).fill(0);
  const list = coerceArrayLike(stamps);
  for (let i = 0; i < list.length; i++) {
    const n = normaliseStamp(list[i]);
    if (!n) continue;
    data[new Date(n.ms).getHours()]++;
  }
  return {
    categories,
    series: [{ name: "Scans", data }],
  };
};

export const loyaltyFunnel = (customers, stamps, redeems, threshold) => {
  const enrolledKeys = new Set();
  for (const c of flattenCustomers(customers)) {
    if (c.emailKey) enrolledKeys.add(c.emailKey);
  }
  const enrolled = enrolledKeys.size;

  const stampCounts = stampsByCustomer(stamps);
  let earnedAtLeastOne = 0;
  let reachedThreshold = 0;
  const safeThreshold =
    typeof threshold === "number" && Number.isFinite(threshold) && threshold > 0
      ? threshold
      : Infinity;
  // Iterate stampCounts so customers present in stamps but not in customers
  // are still counted (defensive: legacy data may diverge slightly).
  for (const n of stampCounts.values()) {
    if (n >= 1) earnedAtLeastOne++;
    if (n >= safeThreshold) reachedThreshold++;
  }

  const redeemList = coerceArrayLike(redeems);
  const redeemedKeys = new Set();
  for (let i = 0; i < redeemList.length; i++) {
    const r = normaliseRedeem(redeemList[i]);
    if (r) redeemedKeys.add(r.emailKey);
  }

  return {
    categories: ["Enrolled", "Earned 1+ stamp", "Reached threshold", "Redeemed 1+"],
    series: [
      {
        name: "Customers",
        data: [enrolled, earnedAtLeastOne, reachedThreshold, redeemedKeys.size],
      },
    ],
  };
};

export const customerEngagementSegments = (customers, stamps, now = Date.now()) => {
  // Bucket customers by their most-recent stamp ms.
  const latestStamp = new Map();
  const list = coerceArrayLike(stamps);
  for (let i = 0; i < list.length; i++) {
    const n = normaliseStamp(list[i]);
    if (!n) continue;
    const prev = latestStamp.get(n.emailKey);
    if (prev == null || n.ms > prev) latestStamp.set(n.emailKey, n.ms);
  }

  const cutoff30 = now - 30 * MS_PER_DAY;
  const cutoff90 = now - 90 * MS_PER_DAY;

  let active = 0;
  let lapsed = 0;
  let dormant = 0;

  for (const c of flattenCustomers(customers)) {
    const ms = latestStamp.get(c.emailKey);
    if (ms == null) {
      dormant++;
      continue;
    }
    if (ms >= cutoff30) active++;
    else if (ms >= cutoff90) lapsed++;
    else dormant++;
  }

  return {
    categories: ["Active (≤30d)", "Lapsed (30-90d)", "Dormant (>90d)"],
    series: [active, lapsed, dormant],
  };
};

export const topCustomers = (stamps, limit = 10) => {
  const counts = stampsByCustomer(stamps);
  const cap = Number.isFinite(limit) && limit > 0 ? Math.floor(limit) : 10;
  const sorted = Array.from(counts.entries())
    .sort((a, b) => b[1] - a[1])
    .slice(0, cap);
  return {
    categories: sorted.map(([emailKey]) => emailKey),
    series: [{ name: "Stamps", data: sorted.map(([, n]) => n) }],
  };
};

// ---------------------------------------------------------------------------
// Drill-down dataset helpers
// ---------------------------------------------------------------------------

export const recentScansForCustomer = (stamps, normalisedEmail) => {
  const target = normaliseEmailKey(normalisedEmail);
  if (!target) return [];
  const list = coerceArrayLike(stamps);
  const rows = [];
  for (let i = 0; i < list.length; i++) {
    const n = normaliseStamp(list[i]);
    if (!n || n.emailKey !== target) continue;
    rows.push({
      email: n.emailKey,
      timestamp: n.ms,
      activity: n.activity,
    });
  }
  rows.sort((a, b) => b.timestamp - a.timestamp);
  return rows;
};

export const customersInSegment = (customers, stamps, segment, now = Date.now()) => {
  const latestStamp = new Map();
  const list = coerceArrayLike(stamps);
  for (let i = 0; i < list.length; i++) {
    const n = normaliseStamp(list[i]);
    if (!n) continue;
    const prev = latestStamp.get(n.emailKey);
    if (prev == null || n.ms > prev) latestStamp.set(n.emailKey, n.ms);
  }
  const cutoff30 = now - 30 * MS_PER_DAY;
  const cutoff90 = now - 90 * MS_PER_DAY;
  const out = [];
  for (const c of flattenCustomers(customers)) {
    const ms = latestStamp.get(c.emailKey);
    const inSegment =
      segment === "active"
        ? ms != null && ms >= cutoff30
        : segment === "lapsed"
          ? ms != null && ms >= cutoff90 && ms < cutoff30
          : ms == null || ms < cutoff90;
    if (!inSegment) continue;
    out.push({
      email: c.emailKey,
      registeredOn: c.raw.registeredOn != null ? String(c.raw.registeredOn) : "",
    });
  }
  return out;
};

// Every scan, sorted ascending (chronological). For the data-export CSV.
export const allScansChronological = (stamps) => {
  const list = coerceArrayLike(stamps);
  const rows = [];
  for (let i = 0; i < list.length; i++) {
    const n = normaliseStamp(list[i]);
    if (!n) continue;
    rows.push({ email: n.emailKey, timestamp: n.ms, activity: n.activity });
  }
  rows.sort((a, b) => a.timestamp - b.timestamp);
  return rows;
};

// Per-customer roll-up: registered date + stamp count + redemption count.
// Includes every customer in `customers/`, plus any orphan emails that only
// appear in stamps or redeems (so the CSV reflects every payable interaction).
// Sorted by stamp count desc, then redemption count desc, then email asc.
export const customerSummaryRows = (customers, stamps, redeems) => {
  const stampCounts = new Map();
  const sList = coerceArrayLike(stamps);
  for (let i = 0; i < sList.length; i++) {
    const n = normaliseStamp(sList[i]);
    if (!n) continue;
    stampCounts.set(n.emailKey, (stampCounts.get(n.emailKey) || 0) + 1);
  }
  const redeemCounts = new Map();
  const rList = coerceArrayLike(redeems);
  for (let i = 0; i < rList.length; i++) {
    const n = normaliseRedeem(rList[i]);
    if (!n) continue;
    redeemCounts.set(n.emailKey, (redeemCounts.get(n.emailKey) || 0) + 1);
  }
  const seen = new Set();
  const out = [];
  for (const c of flattenCustomers(customers)) {
    if (!c.emailKey) continue;
    seen.add(c.emailKey);
    out.push({
      email: c.emailKey,
      registeredOn: c.raw.registeredOn != null ? String(c.raw.registeredOn) : "",
      stamps: stampCounts.get(c.emailKey) || 0,
      redemptions: redeemCounts.get(c.emailKey) || 0,
    });
  }
  for (const key of stampCounts.keys()) {
    if (seen.has(key)) continue;
    seen.add(key);
    out.push({
      email: key,
      registeredOn: "",
      stamps: stampCounts.get(key) || 0,
      redemptions: redeemCounts.get(key) || 0,
    });
  }
  for (const key of redeemCounts.keys()) {
    if (seen.has(key)) continue;
    out.push({
      email: key,
      registeredOn: "",
      stamps: 0,
      redemptions: redeemCounts.get(key) || 0,
    });
  }
  out.sort((a, b) => {
    if (b.stamps !== a.stamps) return b.stamps - a.stamps;
    if (b.redemptions !== a.redemptions) return b.redemptions - a.redemptions;
    return a.email.localeCompare(b.email);
  });
  return out;
};

// Generic recent-scans helper. Returns { email, timestamp, activity } rows
// sorted desc by timestamp, optionally filtered by [sinceMs, untilMs).
export const recentScans = (stamps, options = {}) => {
  const sinceMs = Number.isFinite(options.sinceMs) ? options.sinceMs : -Infinity;
  const untilMs = Number.isFinite(options.untilMs) ? options.untilMs : Infinity;
  const list = coerceArrayLike(stamps);
  const rows = [];
  for (let i = 0; i < list.length; i++) {
    const n = normaliseStamp(list[i]);
    if (!n) continue;
    if (n.ms < sinceMs || n.ms >= untilMs) continue;
    rows.push({ email: n.emailKey, timestamp: n.ms, activity: n.activity });
  }
  rows.sort((a, b) => b.timestamp - a.timestamp);
  return rows;
};

// Recent-redeems helper. Returns { email, timestamp } rows sorted desc.
// `limitMs` keeps the API extensible (e.g. last 30 days); omit for all-time.
export const recentRedeems = (redeems, options = {}) => {
  const sinceMs = Number.isFinite(options.limitMs)
    ? Date.now() - options.limitMs
    : -Infinity;
  const list = coerceArrayLike(redeems);
  const rows = [];
  for (let i = 0; i < list.length; i++) {
    const n = normaliseRedeem(list[i]);
    if (!n) continue;
    if (n.ms < sinceMs) continue;
    rows.push({ email: n.emailKey, timestamp: n.ms });
  }
  rows.sort((a, b) => b.timestamp - a.timestamp);
  return rows;
};

// Customers flagged eligibleToRedeemNow.
export const customersDueToRedeem = (customers) => {
  const out = [];
  for (const c of flattenCustomers(customers)) {
    if (c.raw && c.raw.eligibleToRedeemNow === true) {
      out.push({
        email: c.emailKey,
        registeredOn: c.raw.registeredOn != null ? String(c.raw.registeredOn) : "",
      });
    }
  }
  return out;
};

// Scans falling into a single ISO-week bucket of `scansVsRedemptions12Weeks`.
// `bucketIndex` is 0..11; bucket 11 is the current week.
export const scansInWeekBucket = (stamps, bucketIndex, now = Date.now()) => {
  if (!Number.isFinite(bucketIndex) || bucketIndex < 0 || bucketIndex > 11) return [];
  const weeks = 12;
  const startOfThisWeek = startOfIsoWeek(now);
  const startMs = startOfThisWeek.getTime() - (weeks - 1) * 7 * MS_PER_DAY;
  const bucketStart = startMs + Math.floor(bucketIndex) * 7 * MS_PER_DAY;
  const bucketEnd = bucketStart + 7 * MS_PER_DAY;
  return recentScans(stamps, { sinceMs: bucketStart, untilMs: bucketEnd });
};

// Redemptions in the same 12-week bucket as `scansInWeekBucket`.
export const redeemsInWeekBucket = (redeems, bucketIndex, now = Date.now()) => {
  if (!Number.isFinite(bucketIndex) || bucketIndex < 0 || bucketIndex > 11) return [];
  const weeks = 12;
  const startOfThisWeek = startOfIsoWeek(now);
  const startMs = startOfThisWeek.getTime() - (weeks - 1) * 7 * MS_PER_DAY;
  const bucketStart = startMs + Math.floor(bucketIndex) * 7 * MS_PER_DAY;
  const bucketEnd = bucketStart + 7 * MS_PER_DAY;
  const list = coerceArrayLike(redeems);
  const rows = [];
  for (let i = 0; i < list.length; i++) {
    const n = normaliseRedeem(list[i]);
    if (!n) continue;
    if (n.ms < bucketStart || n.ms >= bucketEnd) continue;
    rows.push({ email: n.emailKey, timestamp: n.ms });
  }
  rows.sort((a, b) => b.timestamp - a.timestamp);
  return rows;
};

// Customers registered within a given month (matches `newSignupsByMonth`
// bucket ordering - index 11 is the current calendar month).
export const customersInMonth = (customers, monthIndex, now = Date.now()) => {
  if (!Number.isFinite(monthIndex) || monthIndex < 0 || monthIndex > 11) return [];
  const nowDate = new Date(now);
  const firstOfThisMonth = new Date(nowDate.getFullYear(), nowDate.getMonth(), 1);
  const target = new Date(
    firstOfThisMonth.getFullYear(),
    firstOfThisMonth.getMonth() - (11 - Math.floor(monthIndex)),
    1
  );
  const targetKey = localMonthKey(target.getTime());
  const out = [];
  for (const c of flattenCustomers(customers)) {
    const ms = parseRegisteredOn(c.raw.registeredOn);
    if (ms == null) continue;
    if (localMonthKey(ms) !== targetKey) continue;
    out.push({
      email: c.emailKey,
      registeredOn: c.raw.registeredOn != null ? String(c.raw.registeredOn) : "",
    });
  }
  return out;
};

// Scans for a particular weekday (0=Mon..6=Sun, matching busiestDayOfWeek).
export const scansByWeekday = (stamps, weekdayIndex) => {
  if (!Number.isFinite(weekdayIndex) || weekdayIndex < 0 || weekdayIndex > 6) return [];
  const list = coerceArrayLike(stamps);
  const rows = [];
  for (let i = 0; i < list.length; i++) {
    const n = normaliseStamp(list[i]);
    if (!n) continue;
    const dow = new Date(n.ms).getDay();
    const idx = dow === 0 ? 6 : dow - 1;
    if (idx !== weekdayIndex) continue;
    rows.push({ email: n.emailKey, timestamp: n.ms, activity: n.activity });
  }
  rows.sort((a, b) => b.timestamp - a.timestamp);
  return rows;
};

// Scans for a particular hour bucket (0..23).
export const scansByHour = (stamps, hour) => {
  if (!Number.isFinite(hour) || hour < 0 || hour > 23) return [];
  const list = coerceArrayLike(stamps);
  const rows = [];
  for (let i = 0; i < list.length; i++) {
    const n = normaliseStamp(list[i]);
    if (!n) continue;
    if (new Date(n.ms).getHours() !== Math.floor(hour)) continue;
    rows.push({ email: n.emailKey, timestamp: n.ms, activity: n.activity });
  }
  rows.sort((a, b) => b.timestamp - a.timestamp);
  return rows;
};

// Customers belonging to a loyalty-funnel stage.
//  0 = Enrolled, 1 = Earned 1+ stamp, 2 = Reached threshold, 3 = Redeemed 1+
export const customersInFunnelStage = (
  customers,
  stamps,
  redeems,
  threshold,
  stageIndex
) => {
  if (!Number.isFinite(stageIndex) || stageIndex < 0 || stageIndex > 3) return [];
  const stampCounts = stampsByCustomer(stamps);
  const safeThreshold =
    typeof threshold === "number" && Number.isFinite(threshold) && threshold > 0
      ? threshold
      : Infinity;
  const redeemList = coerceArrayLike(redeems);
  const redeemedKeys = new Set();
  for (let i = 0; i < redeemList.length; i++) {
    const r = normaliseRedeem(redeemList[i]);
    if (r) redeemedKeys.add(r.emailKey);
  }

  const out = [];
  for (const c of flattenCustomers(customers)) {
    const count = stampCounts.get(c.emailKey) || 0;
    const inStage =
      stageIndex === 0
        ? true
        : stageIndex === 1
          ? count >= 1
          : stageIndex === 2
            ? count >= safeThreshold
            : redeemedKeys.has(c.emailKey);
    if (!inStage) continue;
    out.push({
      email: c.emailKey,
      registeredOn: c.raw.registeredOn != null ? String(c.raw.registeredOn) : "",
    });
  }
  return out;
};

export const customersByDayBucket = (stamps, dayUnixMs) => {
  if (!Number.isFinite(dayUnixMs)) return [];
  const dayStart = startOfLocalDay(dayUnixMs).getTime();
  const dayEnd = dayStart + MS_PER_DAY;
  const list = coerceArrayLike(stamps);
  const rows = [];
  for (let i = 0; i < list.length; i++) {
    const n = normaliseStamp(list[i]);
    if (!n) continue;
    if (n.ms >= dayStart && n.ms < dayEnd) {
      rows.push({ email: n.emailKey, timestamp: n.ms, activity: n.activity });
    }
  }
  rows.sort((a, b) => b.timestamp - a.timestamp);
  return rows;
};

// ---------------------------------------------------------------------------
// Convenience aggregator - one call returns every figure the page needs.
// ---------------------------------------------------------------------------

export const buildDashboardMetrics = (snapshot, now = Date.now()) => {
  const snap = snapshot && typeof snapshot === "object" ? snapshot : {};
  const stamps = snap.stamps;
  const redeems = snap.redeems;
  const customers = snap.customers;
  const threshold = snap.thresholdForRedeeming;

  return {
    kpis: {
      scansToday: scansToday(stamps, now),
      scansLast7Days: scansLast7Days(stamps, now),
      totalScans: totalScans(stamps),
      customersEnrolled: customersEnrolled(customers),
      totalRedemptions: totalRedemptions(redeems),
      dueToRedeemNow: dueToRedeemNow(customers),
    },
    secondary: {
      repeatCustomerRate: repeatCustomerRate(stamps),
      avgStampsPerCustomer: avgStampsPerCustomer(stamps),
      redemptionRate: redemptionRate(redeems, customers),
      activeCustomersLast30Days: activeCustomersLast30Days(stamps, now),
    },
    charts: {
      // scanActivity is recomputed on the page via scanActivityOverTime so the
      // user's range toggle (7d/30d/90d) stays cheap and reactive - no need
      // to eagerly compute three variants here.
      scansVsRedemptions12Weeks: scansVsRedemptions12Weeks(stamps, redeems, now),
      newSignupsByMonth: newSignupsByMonth(customers, now),
      busiestDayOfWeek: busiestDayOfWeek(stamps),
      busiestHourOfDay: busiestHourOfDay(stamps),
      loyaltyFunnel: loyaltyFunnel(customers, stamps, redeems, threshold),
      customerEngagementSegments: customerEngagementSegments(customers, stamps, now),
      topCustomers: topCustomers(stamps, 10),
    },
  };
};
